import { PlanId } from 'src/app/pages/new-billing/types/api.types';
import { LegacyPricingPlans } from 'src/app/pages/new-billing/types/plan.types';
import { EditTimeType, LastSeen, LastTrack, ReportType, UserRole } from './common';
import { IntegrationId } from './task';

export type PricingPlan =
  | PlanId
  | LegacyPricingPlans
  | 'premium_annual'
  | 'premium'
  | 'premium_july20'
  | 'standard_new_annual'
  | 'standard_new'
  | 'standard_july20'
  | 'basic'
  | 'basic_annual'
  | 'basic_new'
  | 'basic_new_annual'
  | 'basic_july20'
  | 'business'
  | 'business_annual'
  | 'enterprise'
  | 'enterprise_annual'
  | '10peruserpermonth'
  | 'resellers';

/** Dashboards that support the sample-data banner and per-dashboard clearing. */
export type DemoDashboardId = 'executive' | 'team' | 'individual' | 'meeting-insights' | 'benchmarksAi' | 'office-vs-remote'
  | 'payroll'
  | 'hours-tracked'
  | 'approvals'
  | 'breaks'
  | 'projects-report'
  | 'timeline'
  | 'unusual-activity'
  | 'screencasts'
  | 'web-app-usage'
  | 'activity-summary'
  | 'attendance'
  | 'internet-connectivity';

export interface CustomUserSettings {
  navatticTutorialCompleted?: boolean;
  visitedNotification?: {
    [key: string]: boolean;
  };
  trackedExperiments?: {
    [key: string]: boolean;
  };
  browserExtensionEnabled?: boolean | 'per-user';
  allowedReports?: {
    [key in ReportType]?: boolean;
  };
  unsubscribedFromEmails?: boolean;
  collapseOnboardingChecklist?: boolean;
  downloadDialogInteracted?: boolean;
  showDemoData?: boolean;
  /** Stable PRNG root for dashboard sample data (set at trial creation when available). */
  demoDataSeed?: number;
  /** Dashboards where the user cleared sample data; real data is shown for those routes only. */
  demoDataHiddenDashboards?: DemoDashboardId[];
}

export enum WebAppTrackingMode {
  Off = 'off',
  Basic = 'basic',
  Extended = 'extended',
  Custom = 'custom',
}

export interface BrowserExtensionSettings {
  integrationSettings?: {
    [key in IntegrationId]: {
      integrationMapping?: number;
    };
  };
}

export type ScreenshotAccess = 'none' | 'owner' | 'owner_admin' | 'owner_manager' | 'owner_admin_manager';

export interface CustomCompanySettings extends CustomUserSettings {
  /** Onboarding focus-area card title, e.g. "Payroll & billing". */
  painpoint?: string;
  splitTest?: { [key: string]: string };
  welcomeSidebarCompleted?: boolean;
  dismissedStep?: {
    [key: string]: boolean;
  };
  completedStep?: {
    [key: string]: boolean;
  };
  completedOnBoarding?: boolean | 'yes';
  /** Date-parseable timestamp for when onboarding was completed. */
  completedOnBoardingAt?: string | number;
  bookCallReady2MinTracked?: boolean;
  bookCallReady5MinTracked?: boolean;
  browserExtensionSettings?: BrowserExtensionSettings;
  hasInviteLink?: boolean;
  dismissedBanners?: {
    [key: string]: boolean;
  };
  keepMyAccount?: boolean;
  trackedSteps?: {
    [key: string]: boolean;
  };
  cancellationFlow?: {
    currentStep?: number;
    selectedReason?: string;
    selectedSubReason?: string;
    finalThoughts?: string;
    lastUpdated?: string;
    contactSupportRequested?: boolean;
  };
  hubspotContactId?: string;
  hubspotIdsSyncedAt?: string;
  nativeIntegrationsVisited?: boolean;
  nativeIntegrationsCalloutDismissed?: boolean;
  integrationsPageSeen?: boolean;
  isEeaOrUkCompany?: boolean;
  isUEMember?: boolean;
  screenshotsDisclaimerAccepted?: boolean;
  enableMacosLocationForSsid?: boolean;
}

interface AutoApproveManualTimeSettings {
  autoApproveManualTimeThresholdSeconds: number;
}

export type Capture2Tracking = 'on' | 'off' | 'user';
export type AiConnectionsTracking = 'on' | 'off' | 'user';

export interface CompanySettings {
  active: boolean;
  allowAdminSID: boolean;
  allowDeleteScreenshots: boolean;
  allowEditTime: boolean;
  allowManagerTagCategories: boolean;
  allowManagerInviteUsers: boolean;
  allowManagerProjectsTasks: boolean;
  allowManagerWorkSchedules: boolean;
  allowUsersActivitySummaryReport: boolean;
  allowManagersExecutiveDashboard: boolean;
  allowLeavesForRegularUsers: boolean;
  allowRegularUsersSetUpTheirShifts: boolean;
  allowUsersUpdateEmail: boolean;
  ssoOnlyAuth: boolean;
  justInTimeProvisioningDisabled: boolean;
  allowNoBreak: boolean;
  autoApproveManualTimeConfiguration?: AutoApproveManualTimeSettings | null;
  disableRegularUsersScreencasts: boolean;
  disableManagersScreencasts: boolean;
  blurScreenshots: boolean;
  capture2Tracking?: Capture2Tracking;
  /** Who may connect Time Doctor data to AI tools via MCP. */
  aiConnectionsTracking?: AiConnectionsTracking;
  /** Coerced from `aiConnectionsTracking` by BE (off → false, else true). */
  enableAiConnections?: boolean;
  canEditTime?: EditTimeType;
  custom: CustomCompanySettings;
  emailReports: string;
  weeklyEmailReports?: boolean;
  employeeId: string;
  exists: boolean;
  firstDayOfWeek: number;
  forceAutostart: boolean;
  trackConnectivity: boolean;
  hideScreencasts: boolean;
  name: string;
  payrollAccess: boolean;
  payrollFeature: boolean;
  workScheduleFeature: boolean;
  allowBreakLimit: boolean;
  allowBreakReminder?: boolean;
  breakReminderIntervalMins?: number;
  billingAccess: boolean;
  poorTimeusePopup: boolean;
  screencastsFeature: boolean;
  screenshots: string | number; // seconds
  showOnReports: boolean;
  signupStep: number;
  stripUrlQuery: boolean;
  tasksMode: 'preset' | 'off';
  trackingMode: 'interactive' | 'silent' | 'mixed';
  trackingType: 'presetTasks' | 'noTasks';
  interactiveTrackingMode?: 'automated' | 'manual';
  videos: 'on' | 'off';
  webAndAppTracking: WebAppTrackingMode;
  workCheckInterval: string | number;
  worklife: WorkLife;
  whoCanAccessScreenshots: ScreenshotAccess;
  whoCanAccessPayroll: string;
  jobTitle?: string;
  contractionBlocker?: CompanyContractionBlockerSettings;
  disableReportsAccess: boolean;
  emailSubCount?: number;
  forceSsoOTP?: boolean;
  disableManualUserProvisioning?: boolean;
  mobileAppTracking?: 'on' | 'off' | 'user';
  requireSilentUserApproval?: boolean;
  syncUsers?: boolean;
  allowGoogleCalendarIntegration?: boolean;
  allowRegularUsersGCIntegration?: boolean;
  allowOutlookCalendarIntegration?: boolean;
  allowRegularUsersOutlookIntegration?: boolean;
  microphoneAccess?: boolean;
  enableLocationForSsid?: boolean;
  /** Onboarding permissions (STAFF-145775) — per-user, so they arrive on userSettings only. */
  allowInviteUsers?: boolean;
  allowManageInstallations?: boolean;
}

export interface CompanySubscription {
  expires: string;
  plan: 'paid' | 'free';
  status: 'paid' | 'free' | 'trial';
  data?: {
    orgId?: '903a8d67-f04b-4fcb-ba2d-32588bd1ae44';
    oAuthClientId?: '936106ca-1993-49f6-96de-de7fae50a090';
    oAuthClientSecret?: string;
    requestorId?: string;
  };
  provider?: 'genesys' | 'stripe' | 'paddle';
  addOns?: string[];
}

export interface GlobalSettings {
  maxGroups?: number;
  beta?: boolean;
}

export interface AuthCompany {
  id: string;
  oldCompanyId: string;
  allUsersTagId: string;
  prefix: string;
  name: string;
  role: UserRole;
  timezone: string;
  companyOwnerEmail: string;
  companyTimezone: string;
  time: string;
  hasManagedScreencasts: boolean;
  hasManagedApprovals: boolean;
  everHadBreak: boolean;
  locked: boolean;

  lastTrack: LastTrack;
  lastSeen: LastSeen;
  userCount: number;
  historicalMaxUserCount: number;
  companyCreatedAt: string;
  tagIds: string[];
  onlyDomainsFor: string[];
  subscription: CompanySubscription;
  minBillableUsers: number;

  companySettings: CompanySettings;
  userSettings: CompanySettings;
  /** @deprecated Use companySettings or userSettings instead. */
  settings: CompanySettings;
  pricingPlan?: PricingPlan;
  billingChangeDate?: string;
  globalSettings?: GlobalSettings;
  splitTest: { name: string, value: any }[];
  silentTrackingTimes: SilentTrackingTimes;
  oauth?: {
    enabled?: boolean;
    provider?: SsoProviderType;
  };
  sso: SsoProviderParams[];
  inviteLink?: string;
  /** Setting keys the server has locked for this company (e.g. via GrowthBook). */
  lockedSettings?: string[];
}

export interface Company {
  id: string;
  name: string;
  createdAt: string;
  creatorId: string;
  parentId: string;
  oldCompanyId: string;
  noWorkspaces: boolean;
  description: string;
  timezone: string;
  locked: boolean;
  userCount: number;
  historicalMaxUserCount: number;
  userDiscount: number;
  uniqueUserCount: number;
  minBillableUsers: number;
  allUsersTagId: string;
  subscription: CompanySubscription;
  settings: CompanySettings;
  splitTest: { name: string, value: any }[];
}

export enum SsoProviderType {
  Okta = 'okta',
  Azure = 'azure',
  WorkOS = 'workos',
  Google = 'google',
}

export interface OAuthCompany {
  provider: SsoProviderType;
  company: string;
  issuer: string;
  clientId: string;
}

export interface SsoProviderParams {
  domain: string[];
  issuer: string;
  clientId: string;
  clientSecret: string;
  redirectURI?: string;
}

export interface SilentTrackingTimes {
  days?: number[];
  hours?: string[][];
  timezone?: string;
}

export interface WorkLife {
  tooManyHours: WorkLifeObject;
  lateHoursAfter: WorkLifeObject;
  hoursOutsideShiftWork: WorkLifeObject;
  weekendWork: WorkLifeObject;
}
export interface WorkLifeObject {
  included: boolean;
  value?: number;
}

export interface CompanyContractionBlockerSettings {
  enabled?: boolean;
  manualLimit: number;
  limit: number;
}
